import { useState } from "react";
import { AlertTriangle, Zap } from "lucide-react";

import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { Button } from "@/core/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { SimulateKycApprovedDialog } from "@/features/notification/components/simulate/simulate-kyc-approved-dialog";
import { SimulateKycExpiredDialog } from "@/features/notification/components/simulate/simulate-kyc-expired-dialog";
import { SimulateKycRejectedDialog } from "@/features/notification/components/simulate/simulate-kyc-rejected-dialog";
import { SimulateTransactionCompletedDialog } from "@/features/notification/components/simulate/simulate-transaction-completed-dialog";
import { SimulateTransactionFailedDialog } from "@/features/notification/components/simulate/simulate-transaction-failed-dialog";
import { useNotificationsTranslation } from "@/features/notification/i18n/use-notifications-translation";

type SimulateKind =
  | "kyc-approved"
  | "kyc-rejected"
  | "kyc-expired"
  | "transaction-completed"
  | "transaction-failed";

/**
 * Card d'outils de simulation d'événements — RÉSERVÉE au compte technique
 * `flashpay-service` (5 endpoints `POST /NotificationEvents/simulate-*`,
 * guide §6.7). Masquée par défaut via `RequireRole` : les autres rôles ne
 * voient rien. Chaque bouton ouvre une dialog dédiée avec l'avertissement
 * d'envoi RÉEL en description (confirmation implicite au submit).
 */
export function SimulateEventsCard() {
  const { t } = useNotificationsTranslation();
  const [openDialog, setOpenDialog] = useState<SimulateKind | null>(null);

  const close = () => setOpenDialog(null);

  return (
    <RequireRole roles={[FLASHPAY_ROLES.SERVICE, FLASHPAY_ROLES.SUPERADMIN]}>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Zap className="h-4 w-4 text-accent-dark" aria-hidden />
            {t("notifications.templates.simulate.title")}
          </CardTitle>
          <CardDescription>
            {t("notifications.templates.simulate.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning-muted px-3 py-2 text-sm text-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>{t("notifications.events.warning")}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setOpenDialog("kyc-approved")}>
              {t("notifications.templates.simulate.kycApproved")}
            </Button>
            <Button variant="outline" onClick={() => setOpenDialog("kyc-rejected")}>
              {t("notifications.templates.simulate.kycRejected")}
            </Button>
            <Button variant="outline" onClick={() => setOpenDialog("kyc-expired")}>
              {t("notifications.templates.simulate.kycExpired")}
            </Button>
            <Button
              variant="outline"
              onClick={() => setOpenDialog("transaction-completed")}
            >
              {t("notifications.templates.simulate.transactionCompleted")}
            </Button>
            <Button
              variant="outline"
              onClick={() => setOpenDialog("transaction-failed")}
            >
              {t("notifications.templates.simulate.transactionFailed")}
            </Button>
          </div>
        </CardContent>
      </Card>

      <SimulateKycApprovedDialog
        open={openDialog === "kyc-approved"}
        onOpenChange={(open) => (open ? setOpenDialog("kyc-approved") : close())}
      />
      <SimulateKycRejectedDialog
        open={openDialog === "kyc-rejected"}
        onOpenChange={(open) => (open ? setOpenDialog("kyc-rejected") : close())}
      />
      <SimulateKycExpiredDialog
        open={openDialog === "kyc-expired"}
        onOpenChange={(open) => (open ? setOpenDialog("kyc-expired") : close())}
      />
      <SimulateTransactionCompletedDialog
        open={openDialog === "transaction-completed"}
        onOpenChange={(open) =>
          open ? setOpenDialog("transaction-completed") : close()
        }
      />
      <SimulateTransactionFailedDialog
        open={openDialog === "transaction-failed"}
        onOpenChange={(open) =>
          open ? setOpenDialog("transaction-failed") : close()
        }
      />
    </RequireRole>
  );
}
